/**
 * D-121c — editable KI-Ordner knowledge pages. One page per intent type
 * (Fragen/Anweisungen/Informationen/Entscheidungen — a projection over
 * rag_entries, Variante A) plus Sessions (conversations → messages). Reached
 * from the KI-Chat header. Editing an entry re-embeds it; deleting is soft.
 *
 * Route: /ki-knowledge?folder=<kiOrdnerId>&kind=<intent|sessions>
 * D-04: no packages/** — reuses the RYFE app shell + api only.
 */
import { useCallback, useEffect, useState } from "react";

import { ApiError, ryfeApi } from "../api";
import { navigate } from "../router";
import { useRyfe } from "../RyfeContext";

import "./ryfe.scss";

import type {
  KiConversation,
  KiIntent,
  KiMessage,
  KiSystemEntry,
} from "../api";

const INTENTS: { kind: KiIntent; label: string }[] = [
  { kind: "frage", label: "Fragen" },
  { kind: "anweisung", label: "Anweisungen" },
  { kind: "information", label: "Informationen" },
  { kind: "entscheidung", label: "Entscheidungen" },
];
const INTENT_KINDS = new Set<string>(INTENTS.map((i) => i.kind));

function query() {
  return typeof window !== "undefined"
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams("");
}

export const RyfeKiKnowledge = () => {
  const { workspace } = useRyfe();
  const wsId = workspace?.id;
  const folder = query().get("folder") || "";
  const kind = query().get("kind") || "frage";

  const [folderName, setFolderName] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!wsId) {
      return;
    }
    ryfeApi
      .listKiFolders(wsId)
      .then((r) =>
        setFolderName(r.folders.find((f) => f.id === folder)?.name || ""),
      )
      .catch(() => undefined);
  }, [wsId, folder]);

  const go = (k: string) =>
    navigate(`/ki-knowledge?folder=${encodeURIComponent(folder)}&kind=${k}`);

  return (
    <div className="ryfe-page ryfe-kiknow">
      <div className="ryfe-kichat__head">
        <h1 className="ryfe-page__title ryfe-kichat__title">
          {folderName || "KI-Ordner"} · Wissen
        </h1>
        <nav className="ryfe-kiknow__tabs">
          {INTENTS.map((i) => (
            <button
              key={i.kind}
              type="button"
              className={kind === i.kind ? "is-active" : ""}
              onClick={() => go(i.kind)}
            >
              {i.label}
            </button>
          ))}
          <button
            type="button"
            className={kind === "sessions" ? "is-active" : ""}
            onClick={() => go("sessions")}
          >
            Sessions
          </button>
          <button
            type="button"
            onClick={() =>
              navigate(`/ki-chat?folder=${encodeURIComponent(folder)}`)
            }
          >
            ← Chat
          </button>
        </nav>
      </div>

      {error && <div className="ryfe-kichat__bubble--error">{error}</div>}

      {!wsId || !folder ? (
        <p>Kein KI-Ordner gewählt.</p>
      ) : kind === "sessions" ? (
        <Sessions wsId={wsId} folder={folder} onError={setError} />
      ) : INTENT_KINDS.has(kind) ? (
        <IntentList
          key={kind}
          wsId={wsId}
          folder={folder}
          kind={kind as KiIntent}
          onError={setError}
        />
      ) : (
        <p>Unbekannte Ansicht.</p>
      )}
    </div>
  );
};

// --- Intent list (projection over rag_entries): edit + soft-delete ---------

const IntentList = ({
  wsId,
  folder,
  kind,
  onError,
}: {
  wsId: string;
  folder: string;
  kind: KiIntent;
  onError: (m: string | null) => void;
}) => {
  const [entries, setEntries] = useState<KiSystemEntry[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const load = useCallback(() => {
    onError(null);
    ryfeApi
      .getKiSystemList(wsId, folder, kind)
      .then((r) => setEntries(r.entries))
      .catch((e) =>
        onError(e instanceof ApiError ? e.message : "Laden fehlgeschlagen"),
      );
  }, [wsId, folder, kind, onError]);

  useEffect(load, [load]);

  const save = async (id: string) => {
    try {
      await ryfeApi.updateKiEntry(wsId, id, folder, draft);
      setEditing(null);
      load();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen");
    }
  };
  const del = async (id: string) => {
    if (!window.confirm("Diesen Eintrag löschen?")) {
      return;
    }
    try {
      await ryfeApi.deleteKiEntry(wsId, id, folder);
      load();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Löschen fehlgeschlagen");
    }
  };

  if (entries.length === 0) {
    return <p className="ryfe-kiknow__empty">Keine Einträge.</p>;
  }
  return (
    <ul className="ryfe-kiknow__list">
      {entries.map((e) => (
        <li key={e.id} className="ryfe-kiknow__row">
          {editing === e.id ? (
            <>
              <textarea
                className="ryfe-cell-input"
                value={draft}
                rows={3}
                onChange={(ev) => setDraft(ev.target.value)}
              />
              <div className="ryfe-kiknow__actions">
                <button type="button" onClick={() => save(e.id)}>
                  Speichern
                </button>
                <button type="button" onClick={() => setEditing(null)}>
                  Abbrechen
                </button>
              </div>
            </>
          ) : (
            <>
              <span className="ryfe-kiknow__content">{e.content}</span>
              <div className="ryfe-kiknow__actions">
                <button
                  type="button"
                  onClick={() => {
                    setEditing(e.id);
                    setDraft(e.content);
                  }}
                >
                  Bearbeiten
                </button>
                <button type="button" onClick={() => del(e.id)}>
                  Löschen
                </button>
              </div>
            </>
          )}
        </li>
      ))}
    </ul>
  );
};

// --- Sessions: conversations → messages (delete session / edit-delete msg) --

const Sessions = ({
  wsId,
  folder,
  onError,
}: {
  wsId: string;
  folder: string;
  onError: (m: string | null) => void;
}) => {
  const [convs, setConvs] = useState<KiConversation[]>([]);
  const [openConv, setOpenConv] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<KiMessage[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const loadConvs = useCallback(() => {
    onError(null);
    ryfeApi
      .listKiConversations(wsId, folder)
      .then((r) => setConvs(r.conversations))
      .catch((e) =>
        onError(e instanceof ApiError ? e.message : "Laden fehlgeschlagen"),
      );
  }, [wsId, folder, onError]);
  useEffect(loadConvs, [loadConvs]);

  const openMessages = async (cid: string) => {
    setOpenConv(cid);
    try {
      const r = await ryfeApi.listKiMessages(wsId, folder, cid);
      setMsgs(r.messages);
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Laden fehlgeschlagen");
    }
  };
  const delConv = async (cid: string) => {
    if (!window.confirm("Ganze Session löschen?")) {
      return;
    }
    try {
      await ryfeApi.deleteKiConversation(wsId, folder, cid);
      if (openConv === cid) {
        setOpenConv(null);
        setMsgs([]);
      }
      loadConvs();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Löschen fehlgeschlagen");
    }
  };
  const saveMsg = async (id: string) => {
    try {
      await ryfeApi.updateKiMessage(wsId, id, folder, draft);
      setEditing(null);
      if (openConv) {
        openMessages(openConv);
      }
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen");
    }
  };
  const delMsg = async (id: string) => {
    if (!window.confirm("Diese Nachricht löschen?")) {
      return;
    }
    try {
      await ryfeApi.deleteKiMessage(wsId, id, folder);
      if (openConv) {
        openMessages(openConv);
      }
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="ryfe-kiknow__sessions">
      <ul className="ryfe-kiknow__list">
        {convs.length === 0 && (
          <li className="ryfe-kiknow__empty">Keine Sessions.</li>
        )}
        {convs.map((c) => (
          <li key={c.conversation_id} className="ryfe-kiknow__row">
            <button
              type="button"
              className="ryfe-kiknow__content"
              onClick={() => openMessages(c.conversation_id)}
            >
              {c.title || c.conversation_id} ({c.message_count})
            </button>
            <div className="ryfe-kiknow__actions">
              <button type="button" onClick={() => delConv(c.conversation_id)}>
                Session löschen
              </button>
            </div>
          </li>
        ))}
      </ul>

      {openConv && (
        <ul className="ryfe-kiknow__list ryfe-kiknow__msgs">
          {msgs.map((m) => {
            const id = String(m.id);
            return (
              <li key={id} className="ryfe-kiknow__row">
                {editing === id ? (
                  <>
                    <textarea
                      className="ryfe-cell-input"
                      value={draft}
                      rows={3}
                      onChange={(ev) => setDraft(ev.target.value)}
                    />
                    <div className="ryfe-kiknow__actions">
                      <button type="button" onClick={() => saveMsg(id)}>
                        Speichern
                      </button>
                      <button type="button" onClick={() => setEditing(null)}>
                        Abbrechen
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="ryfe-kiknow__content">
                      <strong>{m.role}:</strong> {m.content}
                    </span>
                    <div className="ryfe-kiknow__actions">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(id);
                          setDraft(m.content);
                        }}
                      >
                        Bearbeiten
                      </button>
                      <button type="button" onClick={() => delMsg(id)}>
                        Löschen
                      </button>
                    </div>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
